import { ApolloClient, ApolloLink, InMemoryCache, split } from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { setContext } from '@apollo/client/link/context';
import environment from '@lib/environment';
import WebSocket from 'ws';

import AuthModule from '../modules/AuthModule';
import { MockedResponse, MockLink } from '@apollo/client/testing';
import retryLink from './lib/retryLink';
import elevateGqlErrorsLink from './lib/elevateErrorsLink';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';

import graphql from './generated/graphql';
import loggingFetch from './lib/loggingFetch';

const { API_URL } = environment;
export const MAX_RETRY_ATTEMPTS = 5;

const isWebSocketUnavailable =
  typeof window === 'undefined' || typeof window.WebSocket === 'undefined';

const wsLink = new GraphQLWsLink(
  createClient({
    url: API_URL.replace(/^http/, 'ws') + '/graphql',
    connectionParams: async () => {
      const token = await AuthModule.getCurrentToken();
      return {
        Authorization: `Bearer ${token}`,
      };
    },
    retryAttempts: MAX_RETRY_ATTEMPTS,
    webSocketImpl: isWebSocketUnavailable ? WebSocket : undefined,
  }),
);

const httpLink = createUploadLink({
  fetch: loggingFetch,
  uri: ({ operationName }) => `${API_URL}/graphql?opname=${operationName}`,
  headers: { 'GraphQL-preflight': '1' },
});

const authLink = setContext(async (_, { headers }) => {
  const token = (await AuthModule.getCurrentToken()) || '';
  const serviceId = AuthModule.getCurrentServiceId();
  return {
    headers: {
      ...headers,
      'X-ContactCenter-Id': serviceId,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const httpLinkWithMiddleware = ApolloLink.from([
  retryLink(MAX_RETRY_ATTEMPTS),
  elevateGqlErrorsLink,
  authLink,
  httpLink,
]);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  httpLinkWithMiddleware,
);

let globalMocks: readonly MockedResponse<any, any>[] | null = null;
export function setMocks(mocks: readonly MockedResponse<any, any>[] = []) {
  globalMocks = mocks;
}

const client = new ApolloClient({
  link: splitLink,
  cache: createCache(),
});

export function getClient() {
  if (globalMocks) {
    return new ApolloClient({
      link: new MockLink(globalMocks, true),
      cache: createCache(),
    });
  }
  return client;
}

export function createCache() {
  return new InMemoryCache({
    typePolicies: {
      User: {
        keyFields: ['id'],
        merge: true,
        fields: {
          personalLine: {
            merge: true,
          },
        },
      },
      UserPersonalLine: {
        keyFields: false,
      },
      Address: {
        keyFields: false,
      },
      /**
       * Customer merge strategy for multi-instance queries
       *
       * Problem: Same customer query used across multiple sections in customer record (locations, info, contacts)
       * causes cache overwrites where each query instance only returns partial data,
       * resulting in loss of previously fetched fields.
       * Enable merge:true to accumulate all fields from different query responses
       * into a single customer cache entry, preserving data from all query instances.
       */
      Customer: {
        merge: true,
      },
      CallReason: {
        keyFields: ['id', 'tenantId'],
      },
      CalendarRuleSet: {
        keyFields: ['id'],
        fields: {
          rules: {
            merge: false,
          },
        },
      },
      VAAgent: {
        // VA Agent on Follow Ups / Conversations can have different name even if they have the
        // same ID (the name can be changed in the settings).
        // Since there is no way for cache to differentiate between them
        // we should disable keyFields for this type
        keyFields: false,
      },
      AuditInstigator: {
        keyFields: false,
      },
    },
    possibleTypes: graphql.possibleTypes,
  });
}
